<template>
  <div class="card-modal-sidebar__actions" :class="mobileButtonClass">
    <!-- PAUSE ACTION -->
    <Popper
      v-if="isCardActive || isCardCompleted"
      key="pause"
      :title="$t('card_modal.ui.pause_card_confirmation')"
    >
      <template #popper-content>
        <span> Pausar una acción te permite poder sustituirla por otra </span>
      </template>
      <template #popper-trigger>
        <SubmitButton
          data-action="pause-action"
          :loading="isBeingPaused"
          :spinner-size="isMobile ? 22 : 25"
        >
          <AppIcon icon="far fa-pause-circle" />
          {{ $t('card_modal.ui.pause_card') }}
        </SubmitButton>
      </template>
      <!-- action -->
      <template #popper-action="{ closePopper }">
        <AppActionButton @click="handleAction('paused'), closePopper()">
          {{ $t('card_modal.ui.pause_card') }}
        </AppActionButton>
      </template>
    </Popper>

    <!-- ACTIVATE ACTION -->
    <Popper
      v-if="isCardPaused || isCardCompleted"
      key="activate"
      :title="$t('card_modal.ui.activate_card_confirmation')"
    >
      <template #popper-trigger>
        <SubmitButtonSlot
          data-action="activate-action"
          :disabled="!hasEnoughSlots"
          :show-message="!hasEnoughSlots"
          :tooltip-message="$t('ui.tooltips.insufficient_slots')"
          :loading="isBeingActivated"
          :spinner-size="isMobile ? 22 : 25"
        >
          <AppIcon icon="far fa-play-circle" />
          {{ $t('card_modal.ui.activate_card') }}
        </SubmitButtonSlot>
      </template>
      <!-- action -->
      <template #popper-action="{ closePopper }">
        <AppActionButton @click="hasEnoughSlots && handleAction('active'), closePopper()">
          {{ $t('card_modal.ui.activate_card') }}
        </AppActionButton>
      </template>
    </Popper>
    <!-- FINISH ACTION -->
    <Popper
      v-if="isCardActive || isCardPaused"
      v-allowed:employee
      :title="$t('card_modal.ui.finish_card_confirmation')"
    >
      <template #popper-trigger>
        <SubmitButton
          data-action="finish-action"
          :show-message="false"
          :loading="isBeingCompleted"
          :spinner-size="isMobile ? 22 : 25"
        >
          <AppIcon icon="fas fa-flag-checkered" />
          {{ $t('card_modal.ui.finish_card') }}
        </SubmitButton>
      </template>
      <!-- action -->
      <template #popper-action="{ closePopper }">
        <AppActionButton @click="handleAction('completed'), closePopper()">
          {{ $t('card_modal.ui.finish_card') }}
        </AppActionButton>
      </template>
    </Popper>
  </div>
</template>

<script>
// Utilities
import { checkApiResponse } from 'Utils'
import MobileBreakpointControl from 'Mixins/MobileBreakpointControl'
// Components
import AppIcon from 'Ui/AppIcon'
import Popper from 'Modules/Popper'
import AppActionButton from 'Ui/AppActionButton'
import SubmitButton from 'Components/SubmitButton'
import SubmitButtonSlot from 'Components/SubmitButtonSlot'

export default {
  name: 'CardModalSidebarActions',

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  components: {
    Popper,
    AppIcon,
    SubmitButton,
    AppActionButton,
    SubmitButtonSlot,
  },

  mixins: [MobileBreakpointControl],

  data() {
    return {
      isBeingActivated: false,
      isBeingPaused: false,
      isBeingCompleted: false,
    }
  },

  methods: {
    toggleActionStatus(requestedAction) {
      const action = this.checkRequestedStatus(requestedAction)
      this[action] = !this[action]
    },

    checkRequestedStatus(requestedAction) {
      let action
      switch (requestedAction) {
        case 'paused':
          action = 'isBeingPaused'
          break
        case 'active':
          action = 'isBeingActivated'
          break
        case 'completed':
          action = 'isBeingCompleted'
          break
      }
      return action
    },

    async handleAction(requestedAction) {
      this.toggleActionStatus(requestedAction)

      const cardStatusInfo = {
        id: this.$route.params.card_id,
        newValues: {
          status: requestedAction,
        },
      }

      const update = await this.$store.dispatch('cards/updateCard', cardStatusInfo)

      if (!checkApiResponse(update)) return this.toggleActionStatus(requestedAction)

      this.$store.dispatch('modal/close')
    },
  },

  computed: {
    isCardPaused() {
      return this.card.status === 'paused'
    },

    isCardCompleted() {
      return this.card.status === 'completed'
    },

    isCardActive() {
      return this.card.status === 'active'
    },

    mobileButtonClass() {
      return this.isMobile && 'compact'
    },

    hasEnoughSlots() {
      const availableSlots = this.$store.getters['client/getAvailableSlots']
      return availableSlots - this.card.slots >= 0
    },
  },
}
</script>

<style scoped>
.card-modal-sidebar__actions {
  display: grid;
  gap: 10px;
}

.card-modal-sidebar__actions >>> .submit-button .app-icon {
  margin-right: 10px;
}

.compact {
  justify-items: flex-start;
  gap: 0;
}

.compact >>> .submit-button {
  width: 100%;
}

.compact >>> .submit-button .app-button {
  justify-content: flex-start;
  min-height: unset;
  padding: 10px;
}

.compact >>> .submit-button .app-button:hover {
  background-color: var(--rgba-white-400);
}

.compact >>> .submit-button .app-icon {
  color: #fff;
}

.app-action-button {
  display: block;
  max-width: 100%;
}

/* Responsive ---------- */
@media (min-width: 681px) {
  .card-modal-sidebar__actions {
    align-self: flex-end;
  }
}

@media (max-width: 680px) {
  .card-modal-sidebar__actions {
    margin-top: -10px;
  }

  .compact >>> .submit-button .app-button {
    color: var(--c-primary);
    background-color: transparent;
  }

  .card-modal-sidebar__actions >>> .submit-button .app-icon {
    color: var(--c-secondary);
  }
}
</style>
